import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useActiveSession } from '../api/active/sessionProvider';
import {
  canUseCatalogAction,
  canUsePackageGroupAction,
} from '../api/formal/phase1Permissions';
import {
  type EquipmentPageRuntime,
  useEquipmentPageRuntime,
} from '../equipment-integration/equipmentPageRuntime';
import { createPackageApi } from './packageApi';
import { createPackageGroupApi } from './packageGroupApi';
import { createPlanMembershipApi } from './planMembershipApi';
import type { PlanMembershipPort } from './planMembershipPort';

export type PackagePageRuntime = {
  api: ReturnType<typeof createPackageApi>;
  groupApi: ReturnType<typeof createPackageGroupApi>;
  equipmentApi: EquipmentPageRuntime['api'];
  planMembership: PlanMembershipPort | null;
  canReadEquipment?: boolean;
  canManagePlanMembership?: boolean;
  canRead: boolean;
  canReadGroups: boolean;
  canManageGroups: boolean;
  canCreateGroups: boolean;
  canManage: boolean;
  canCreate?: boolean;
  canSetStatus?: boolean;
  /** 阶段一：删除只允许管理员部门，服务端仍会再次校验。 */
  canDelete: boolean;
  recoverUnauthorized(): Promise<void>;
};

const Context = createContext<PackagePageRuntime | null>(null);

export function PackagePageRuntimeProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: PackagePageRuntime;
}) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function ActivePackagePageRuntimeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { state, transport, refresh } = useActiveSession();
  const equipmentRuntime = useEquipmentPageRuntime();
  const ready = state.status === 'ready' ? state : null;
  const value = useMemo<PackagePageRuntime>(
    () => ({
      api: createPackageApi(transport, ready?.csrfToken ?? null),
      groupApi: createPackageGroupApi(transport, ready?.csrfToken ?? null),
      canReadGroups: canUsePackageGroupAction(ready?.user, 'read'),
      canManageGroups: canUsePackageGroupAction(ready?.user, 'manage'),
      canCreateGroups: canUsePackageGroupAction(ready?.user, 'manage'),
      equipmentApi: equipmentRuntime.api,
      canReadEquipment: canUseCatalogAction(ready?.user, 'equipment', 'read'),
      canManagePlanMembership:
        canUseCatalogAction(ready?.user, 'plan', 'edit') &&
        canUseCatalogAction(ready?.user, 'plan', 'read'),
      planMembership: createPlanMembershipApi(
        transport,
        ready?.csrfToken ?? null,
      ),
      canRead: canUseCatalogAction(ready?.user, 'package', 'read'),
      canManage: canUseCatalogAction(ready?.user, 'package', 'edit'),
      canCreate: canUseCatalogAction(ready?.user, 'package', 'create'),
      canSetStatus: canUseCatalogAction(ready?.user, 'package', 'set_status'),
      canDelete: canUseCatalogAction(ready?.user, 'package', 'delete'),
      recoverUnauthorized: refresh,
    }),
    [equipmentRuntime.api, ready, refresh, transport],
  );
  if (!ready) return null;
  return (
    <PackagePageRuntimeProvider value={value}>
      {children}
    </PackagePageRuntimeProvider>
  );
}

export function usePackagePageRuntime() {
  const value = useContext(Context);
  if (!value) throw new Error('PackagePageRuntimeProvider is required');
  return value;
}

export function useOptionalPackagePageRuntime() {
  return useContext(Context);
}
